import React, { useContext, useEffect, useState, useRef } from 'react';
// ROUTING
import ReactDOM from 'react-dom';
import { AuthProvider, TAuthConfig, AuthContext } from 'react-oauth2-code-pkce';
// REDUX
import { Provider, useDispatch, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

// ANT DESIGN
import 'antd/dist/antd.css';
import { ConfigProvider, Spin, Button } from 'antd';
import ptBR from 'antd/es/locale/pt_BR';
import { store, persistor, ApplicationState } from './store';
// ACTIONS
import {
	limpaLogin,
	logOffRequest,
} from './store/modules/api/sgu/loginUnico/actions';
import App from './App';
import Login from './pages/Login';

const testMode =
	process.env.REACT_APP_ENVIRONMENT !== undefined &&
	process.env.REACT_APP_ENVIRONMENT === 'TEST';

const { origin } = window.location;

let idpSpDomain = '';
let cyberArkBaseUrl = '';
let cyberArkCustomerId = '';

if (
	origin === 'https://www.poupatempo4g.des.sp.gov.br' ||
	origin === 'https://www.homologa.poupatempo4g.sp.gov.br' ||
	origin === 'http://localhost:3000' ||
	origin === 'https://10.200.13.36' ||
	origin === 'https://10.200.13.37'
) {
	// AMBIENTE HML / DEV
	idpSpDomain = 'https://rhsso.idp-hml.sp.gov.br';
	cyberArkBaseUrl = 'https://hml-prodesp.id.cyberark.cloud';
	cyberArkCustomerId = 'abc4967';
} else {
	idpSpDomain = 'https://idp.sp.gov.br';
	cyberArkBaseUrl = 'https://prodesp.id.cyberark.cloud';
	cyberArkCustomerId = 'abc4966';
}

interface ICyberArkConfig {
	clientId: string;
	appId: string;
	env: string;
}

const prodConfig: ICyberArkConfig = {
	clientId: '34752d0a-55cb-4e23-9f53-71a528ecb073',
	appId: 'balcaounico',
	env: 'prd',
};

const envConfigs: { [key: string]: ICyberArkConfig } = {
	'https://www.homologa.poupatempo4g.sp.gov.br': {
		clientId: 'a8c6dceb-4f74-45cb-a8e6-20bf0fd4f6a3',
		appId: 'balcaounico_hml',
		env: 'hml',
	},
	'https://www.poupatempo4g.des.sp.gov.br': {
		clientId: '81953935-ca85-49a7-9f2f-643fe4db27e5',
		appId: 'balcaounico_dev',
		env: 'des',
	},
	'http://localhost:3000': {
		clientId: '7655293b-a364-4d85-94ab-dec5ee1fbeb9',
		appId: 'balcaounico_local',
		env: 'hml',
	},
	'https://www.poupatempo4g.sp.gov.br': prodConfig,
};

const currentEnv = envConfigs[origin] || prodConfig;

const getCleanOrigin = () => {
	const url = window.location.origin;
	return url.endsWith('/') ? url.slice(0, -1) : url;
};

const scopes = [
	'openid',
	'email',
	'profile',
	'govbr_confiabilidades',
	'api:mp.paternidade',
	'api:mp.paternidade.portal',
	'api:integrador.seduc-responsaveis.consultas',
	'api:integrador.seduc-responsaveis.atualizacoes',
	'api:integrador.seduc-dominios.consultas',
	'api:integrador.seduc-inscricoes.consultas',
	'api:integrador.seduc-inscricoes.atualizacoes',
	'api:integrador.seduc-fichas.consultas',
	'api:integrador.seduc-alunos.consultas',
	'api:integrador.seduc-fichas.atualizacoes',
	'api:cdesp.bcadastro.fisica.cpf.search',
	'api:cdesp.bcadastro.juridica.cnpj.search',
	'api:integrador.poupafila.consultas',
	'api:cdhu.mutuarios',
	'api:integrador.bu-ppt-dominios.read',
	'api:integrador.bu-ppt-dominios.upsert',
	'api:integrador.iirgd-bu-ppt.read',
	'api:integrador.iirgd-bu-ppt.consultas',
	'api:integrador.iirgd-bu-ppt.upsert',
	'api:integrador.bu-ppt-prefeituras.cadastros',
	'api:integrador.bu-ppt-prefeituras.consultas',
	'api:integrador.ppt-prefeituras-canais.consultas',
	'api:iirgd.atestado.simples',
	'api:iirgd.atestado.completo',
	'api:integrador.iirgd-atestados.central.liberacao',
	'api:integrador.iirgd-atestados.validacao',
	'api:integrador.iirgd-bu-ppt.atualizacoes',
	'api:integrador.iirgd-bu-ppt.atualizacao.senha',
	'api:integrador.bu-ppt-relatorios.read',
	'api:integrador.bu-ppt-relatorios.upsert',
	'api:rotasp.iirgd.solicitacoes',
	'api:integrador.bu-ppt-sgu.read',
	'api:integrador.bu-ppt-sgu.upsert',
	'api:integrador.iirgd-solicitacoes.bloqueiorg',
	'api:integrador.biometria.verificacao',
	'api:integrador.biometria.imagens.foto',
	'api:integrador.biometria.verificacao.iirgd',
	'api:integrador.biometria.verificacao.facial',
];

// --- 1. CONFIG: CYBERARK FLOW ---
const CYBERARK_RUN_URL = `${cyberArkBaseUrl}/run?appkey=${currentEnv.clientId}&customerId=${cyberArkCustomerId}&username=user%40idpsp&iwa=false&submitUsername=true`;

const authConfigCyberArk: TAuthConfig = {
	clientId: currentEnv.clientId,
	authorizationEndpoint: `${cyberArkBaseUrl}/OAuth2/Authorize/${currentEnv.appId}`,
	tokenEndpoint: `${cyberArkBaseUrl}/OAuth2/Token/${currentEnv.appId}`,
	logoutRedirect: `${origin}`,
	redirectUri: getCleanOrigin(),
	scope: 'all',
	autoLogin: false,
};

const authConfigDirect: TAuthConfig = {
	clientId: 'poupatempo-balcaounico4.0',
	authorizationEndpoint: `${idpSpDomain}/auth/realms/idpsp/protocol/openid-connect/auth`,
	tokenEndpoint: `${idpSpDomain}/auth/realms/idpsp/protocol/openid-connect/token`,
	logoutEndpoint: `${idpSpDomain}/auth/realms/idpsp/protocol/openid-connect/logout`,
	redirectUri: `${origin}`,
	logoutRedirect: `${origin}`,
	scope: scopes.join(' '),
	autoLogin: false,
	onRefreshTokenExpire: () => null,
};

const authConfigIdpSpStep2: TAuthConfig = {
	clientId: 'poupatempo-balcaounico4.0',
	authorizationEndpoint: `${idpSpDomain}/auth/realms/idpsp/protocol/openid-connect/auth`,
	tokenEndpoint: `${idpSpDomain}/auth/realms/idpsp/protocol/openid-connect/token`,
	logoutEndpoint: `${idpSpDomain}/auth/realms/idpsp/protocol/openid-connect/logout`,
	redirectUri: `${origin}`,
	logoutRedirect: `${origin}`,
	scope: scopes.join(' '),
	autoLogin: false,
};

const CyberArkManager: React.FC<{ onSuccess: (token: string) => void }> = ({
	onSuccess,
}) => {
	const { token, login, loginInProgress, error } = useContext(AuthContext);

	useEffect(() => {
		if (token) {
			onSuccess(token);
			return;
		}
		if (error) {
			console.error('Erro CyberArk:', error);
			sessionStorage.removeItem('cyberark_run_completed');
			return;
		}
		if (!token && !loginInProgress) {
			const hasCode = window.location.search.includes('code=');
			const runStepDone = sessionStorage.getItem('cyberark_run_completed');

			if (!hasCode) {
				if (!runStepDone) {
					console.log('Iniciando Etapa 1: CyberArk /run');
					sessionStorage.setItem('cyberark_run_completed', 'true');
					window.location.href = CYBERARK_RUN_URL;
				} else {
					login();
				}
			}
		}
	}, [token, loginInProgress, error, login, onSuccess]);

	if (error) {
		return (
			<div
				style={{
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<h2 style={{ color: 'red' }}>Erro CyberArk</h2>
				<p>{error}</p>
				<Button
					onClick={() => {
						sessionStorage.clear();
						window.location.href = window.location.origin;
					}}
				>
					Reiniciar
				</Button>
			</div>
		);
	}

	return (
		<div
			style={{
				height: '100vh',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				flexDirection: 'column',
			}}
		>
			<Spin size="large" />
			<p style={{ marginTop: 20 }}>Conectando ao CyberArk...</p>
		</div>
	);
};

const AutoLoginDirectTrigger: React.FC = () => {
	const { login, token, loginInProgress } = useContext(AuthContext);
	const ranRef = useRef(false);

	useEffect(() => {
		if (token || loginInProgress || ranRef.current) return;
		if (!token && !loginInProgress) {
			console.log('AutoLoginDirectTrigger: Iniciando autenticação...');
			ranRef.current = true;
			login();
		}
	}, [login, token, loginInProgress]);

	return null;
};

const TokenRepairGuard: React.FC = () => {
	const { login, token, loginInProgress } = useContext(AuthContext);
	const attemptRef = useRef(false);

	useEffect(() => {
		if (loginInProgress || attemptRef.current) return;

		const storedToken = localStorage.getItem('ROCP_token');
		const storedIdToken = localStorage.getItem('ROCP_idToken');

		if (storedToken) {
			if (!storedIdToken || !token) {
				console.log('TokenRepairGuard: Restaurando sessão incompleta...');
				attemptRef.current = true;
				login();
			}
		}
	}, [login, token, loginInProgress]);

	return null;
};

const LogoutManager: React.FC = () => {
	const dispatch = useDispatch();
	const { reload } = useSelector(
		(state: ApplicationState) => state.api.sgu.loginUnico,
	);

	useEffect(() => {
		const isCallback = window.location.search.includes('code=');

		if (reload && !isCallback) {
			const activeFlow = sessionStorage.getItem('active_flow');

			let idToken = localStorage.getItem('ROCP_idToken');
			if (idToken && idToken.startsWith('"') && idToken.endsWith('"')) {
				idToken = idToken.slice(1, -1);
			}

			console.log(
				'LogoutManager: Logout solicitado via Redux. Fluxo:',
				activeFlow,
			);

			dispatch(logOffRequest());

			if (
				(activeFlow === 'DIRECT' || activeFlow === 'CYBERARK_STEP2') &&
				idToken
			) {
				console.log('LogoutManager: Executando POST Logout...');

				localStorage.clear();
				sessionStorage.clear();

				const logoutUrl = `${idpSpDomain}/auth/realms/idpsp/protocol/openid-connect/logout`;
				const redirectUri = window.location.origin;

				const form = document.createElement('form');
				form.method = 'POST';
				form.action = logoutUrl;

				const inputToken = document.createElement('input');
				inputToken.type = 'hidden';
				inputToken.name = 'id_token_hint';
				inputToken.value = idToken;
				form.appendChild(inputToken);

				const inputRedirect = document.createElement('input');
				inputRedirect.type = 'hidden';
				inputRedirect.name = 'post_logout_redirect_uri';
				inputRedirect.value = redirectUri;
				form.appendChild(inputRedirect);

				document.body.appendChild(form);
				form.submit();
			} else {
				localStorage.clear();
				sessionStorage.clear();
				window.location.href = window.location.origin;
			}
		}
	}, [reload, dispatch]);

	return null;
};

const Step2Protector: React.FC = ({ children }) => {
	const { token } = useContext(AuthContext);

	if (!token) {
		return (
			<div
				style={{
					height: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					flexDirection: 'column',
				}}
			>
				<Spin size="large" />
				<p style={{ marginTop: 20 }}>Conectando ao Gov.br...</p>
			</div>
		);
	}
	return <>{children}</>;
};

const AuthOrchestrator: React.FC = () => {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(limpaLogin());
	}, [dispatch]);

	const [activeFlow, setActiveFlow] = useState<string | null>(() => {
		const savedFlow = sessionStorage.getItem('active_flow');

		const storedToken = localStorage.getItem('ROCP_token');
		if (storedToken) {
			console.log('AuthOrchestrator: Token encontrado. Restaurando DIRECT.');
			return savedFlow || 'DIRECT';
		}

		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('code')) {
			return savedFlow || 'DIRECT';
		}

		const { referrer } = document;
		if (referrer.includes('minhaarea.sp.gov.br') && !savedFlow) {
			sessionStorage.setItem('active_flow', 'DIRECT');
			return 'DIRECT';
		}

		const savedToken = sessionStorage.getItem('cyberark_token_saved');
		if (savedToken) return 'CYBERARK_STEP2';

		return savedFlow;
	});

	const handleCyberArkFinish = (token: string) => {
		sessionStorage.setItem('cyberark_token_saved', token);
		sessionStorage.setItem('should_auto_login_idpsp', 'true');
		sessionStorage.setItem('active_flow', 'CYBERARK_STEP2');
		sessionStorage.removeItem('cyberark_run_completed');

		const cleanUrl = window.location.origin + window.location.pathname;
		window.history.replaceState({}, document.title, cleanUrl);

		setActiveFlow('CYBERARK_STEP2');
	};

	const selectFlow = (flowType: 'CYBERARK' | 'DIRECT') => {
		sessionStorage.setItem('active_flow', flowType);
		setActiveFlow(flowType);
	};

	if (!activeFlow) {
		return <Login onSelectAuthFlow={selectFlow} />;
	}

	if (activeFlow === 'CYBERARK') {
		return (
			<AuthProvider authConfig={authConfigCyberArk}>
				<CyberArkManager onSuccess={handleCyberArkFinish} />
			</AuthProvider>
		);
	}

	if (activeFlow === 'CYBERARK_STEP2') {
		return (
			<AuthProvider authConfig={authConfigIdpSpStep2}>
				<TokenRepairGuard />
				<LogoutManager />
				<Step2Protector>
					<App />
				</Step2Protector>
			</AuthProvider>
		);
	}

	if (activeFlow === 'DIRECT') {
		return (
			<AuthProvider authConfig={authConfigDirect}>
				<AutoLoginDirectTrigger />
				<LogoutManager />
				<App />
			</AuthProvider>
		);
	}

	return null;
};

ReactDOM.render(
	<Provider store={store}>
		<PersistGate loading={null} persistor={persistor}>
			<ConfigProvider locale={ptBR}>
				{testMode ? <App /> : <AuthOrchestrator />}
			</ConfigProvider>
		</PersistGate>
	</Provider>,
	document.getElementById('root'),
);
